import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface Content {
  id: string;
  title: string;
  category: string;
  description?: string;
  image_url?: string;
  thumbnail_url?: string;
  content_url?: string;
  user_id: string;
  status: string;
  is_visible: boolean;
  talent_reward: number;
  created_at: string;
  view_count: number;
  like_count: number;
  comment_count: number;
  tags: string[];
  file_urls: string[];
  profiles?: {
    full_name: string;
  };
}

interface Comment {
  id: string;
  content_id: string;
  user_id: string;
  comment: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string;
  };
  replies?: Comment[];
}

export default function ContentPreview() {
  const { user } = useAuth();
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  useEffect(() => {
    const fetchContents = async () => {
      try {
        setLoading(true);
        
        // 모든 콘텐츠 가져오기
        const { data, error } = await supabase
          .from('contents')
          .select(`
            *,
            profiles:author_id (
              id,
              full_name,
              avatar_url
            )
          `)
          .eq('status', 'published')
          .order('like_count', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        // 클라이언트 측에서 YouTube 영상이 있는 콘텐츠를 우선 정렬
        const sortedData = (data || []).sort((a, b) => {
          const aHasYouTube = a.content_url && getYouTubeVideoId(a.content_url) ? 1 : 0;
          const bHasYouTube = b.content_url && getYouTubeVideoId(b.content_url) ? 1 : 0;
          
          // YouTube 영상이 있는 것을 우선
          if (aHasYouTube !== bHasYouTube) {
            return bHasYouTube - aHasYouTube;
          }
          
          // 같은 조건이면 좋아요 수로 정렬
          if (b.like_count !== a.like_count) {
            return b.like_count - a.like_count;
          }
          
          // 그 다음 최신순
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        // 상위 20개만 선택
        setContents(sortedData.slice(0, 20));
      } catch (error) {
        console.error('콘텐츠 로딩 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContents();
  }, []);

  const getContentThumbnail = (content: Content) => {
    if (content.file_urls && content.file_urls.length > 0) {
      const imageFile = content.file_urls.find(url => 
        url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
      );
      if (imageFile) return imageFile;
    }

    if (content.thumbnail_url) return content.thumbnail_url;
    if (content.image_url) return content.image_url;

    if (content.content_url) {
      const url = content.content_url.toLowerCase();
      
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = '';
        if (url.includes('youtube.com/watch?v=')) {
          videoId = content.content_url.split('v=')[1]?.split('&')[0];
        } else if (url.includes('youtu.be/')) {
          videoId = content.content_url.split('youtu.be/')[1]?.split('?')[0];
        }
        if (videoId) {
          return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }
      
      if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
        return content.content_url;
      }
    }

    const query = `${content.title} ${content.description || ''} christian faith spiritual content simple background peaceful atmosphere soft lighting minimalist design religious theme inspirational message clean aesthetic modern style warm tones serene mood`.substring(0, 200);
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28query%29%7D&width=400&height=300&seq=content-${content.id}&orientation=landscape`;
  };

  const handleContentClick = async (content: Content) => {
    setSelectedContent(content);
    setShowDetailModal(true);
    
    // 조회수 증가
    await recordView(content.id);
    
    // 좋아요 상태 확인
    if (user) {
      await checkLikeStatus(content.id);
    }
    
    // 댓글 불러오기
    await fetchComments(content.id);
  };

  const handleViewAll = () => {
    window.REACT_APP_NAVIGATE('/content-explore');
  };

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    
    const urlLower = url.toLowerCase();
    let videoId = '';
    
    if (urlLower.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (urlLower.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    return videoId || null;
  };

  const recordView = async (contentId: string) => {
    try {
      await supabase
        .from('content_views')
        .upsert({
          content_id: contentId,
          user_id: user?.id || null
        }, {
          onConflict: 'content_id,user_id'
        });

      const { data } = await supabase
        .from('content_views')
        .select('id')
        .eq('content_id', contentId);

      const viewCount = data?.length || 0;

      await supabase
        .from('contents')
        .update({ view_count: viewCount })
        .eq('id', contentId);

      setContents(prev => prev.map(c => 
        c.id === contentId ? { ...c, view_count: viewCount } : c
      ));
    } catch (error) {
      console.error('조회수 기록 실패:', error);
    }
  };

  const checkLikeStatus = async (contentId: string) => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from('content_likes')
        .select('id')
        .eq('content_id', contentId)
        .eq('user_id', user.id)
        .single();

      setIsLiked(!!data);
    } catch (error) {
      setIsLiked(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!selectedContent) return;

    try {
      if (isLiked) {
        await supabase
          .from('content_likes')
          .delete()
          .eq('content_id', selectedContent.id)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('content_likes')
          .insert({
            content_id: selectedContent.id,
            user_id: user.id
          });
      }

      const { data } = await supabase
        .from('content_likes')
        .select('id')
        .eq('content_id', selectedContent.id);

      const likeCount = data?.length || 0;

      await supabase
        .from('contents')
        .update({ like_count: likeCount })
        .eq('id', selectedContent.id);

      setIsLiked(!isLiked);
      setSelectedContent({ ...selectedContent, like_count: likeCount });
      setContents(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, like_count: likeCount } : c
      ));
    } catch (error) {
      console.error('좋아요 처리 실패:', error);
    }
  };

  const fetchComments = async (contentId: string) => {
    try {
      const { data, error } = await supabase
        .from('content_comments')
        .select(`
          *,
          profiles (
            full_name
          )
        `)
        .eq('content_id', contentId)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const commentsWithReplies = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: replies } = await supabase
            .from('content_comments')
            .select(`
              *,
              profiles (
                full_name
              )
            `)
            .eq('parent_id', comment.id)
            .order('created_at', { ascending: true });

          return { ...comment, replies: replies || [] };
        })
      );

      setComments(commentsWithReplies);
    } catch (error) {
      console.error('댓글 조회 실패:', error);
    }
  };

  const handleAddComment = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!selectedContent || !newComment.trim()) return;

    try {
      const { error } = await supabase
        .from('content_comments')
        .insert({
          content_id: selectedContent.id,
          user_id: user.id,
          comment: newComment,
          parent_id: replyTo
        });

      if (error) throw error;

      const { data } = await supabase
        .from('content_comments')
        .select('id')
        .eq('content_id', selectedContent.id);

      const commentCount = data?.length || 0;

      await supabase
        .from('contents')
        .update({ comment_count: commentCount })
        .eq('id', selectedContent.id);

      setNewComment('');
      setReplyTo(null);
      fetchComments(selectedContent.id);
      setSelectedContent({ ...selectedContent, comment_count: commentCount });
      setContents(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, comment_count: commentCount } : c
      ));
    } catch (error) {
      console.error('댓글 작성 실패:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('댓글을 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('content_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      if (selectedContent) {
        fetchComments(selectedContent.id);
        
        const { data } = await supabase
          .from('content_comments')
          .select('id')
          .eq('content_id', selectedContent.id);

        const commentCount = data?.length || 0;

        await supabase
          .from('contents')
          .update({ comment_count: commentCount })
          .eq('id', selectedContent.id);

        setSelectedContent({ ...selectedContent, comment_count: commentCount });
        setContents(prev => prev.map(c => 
          c.id === selectedContent.id ? { ...c, comment_count: commentCount } : c
        ));
      }
    } catch (error) {
      console.error('댓글 삭제 실패:', error);
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-teal-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            인기 신앙 콘텐츠
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            많은 사랑을 받고 있는 신앙 콘텐츠를 만나보세요
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-600">로딩 중...</p>
          </div>
        ) : contents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            콘텐츠가 없습니다.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {contents.map((content) => (
                <div
                  key={content.id}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-100"
                  onClick={() => handleContentClick(content)}
                >
                  <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
                    <img
                      src={getContentThumbnail(content)}
                      alt={content.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full text-sm font-medium text-teal-600">
                      무료
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-teal-50 text-teal-600 text-xs rounded-full">
                        {content.category}
                      </span>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2 hover:text-teal-600 transition-colors cursor-pointer line-clamp-1">
                      {content.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {content.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <i className="ri-eye-line"></i>
                          <span>{content.view_count || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <i className="ri-heart-line"></i>
                          <span>{content.like_count || 0}</span>
                        </div>
                        {getYouTubeVideoId(content.content_url || '') && (
                          <div className="flex items-center space-x-1 text-red-600">
                            <i className="ri-play-circle-fill"></i>
                          </div>
                        )}
                        {content.content_url && !getYouTubeVideoId(content.content_url) && (
                          <div className="flex items-center space-x-1 text-blue-600">
                            <i className="ri-link"></i>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{new Date(content.created_at).toLocaleDateString('ko-KR')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <button
                onClick={handleViewAll}
                className="px-8 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                더 보기
              </button>
            </div>
          </>
        )}
      </div>

      {/* 상세보기 모달 */}
      {showDetailModal && selectedContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full">
                    {selectedContent.category}
                  </span>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <i className="ri-eye-line"></i>
                      <span>{selectedContent.view_count || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <i className="ri-heart-line"></i>
                      <span>{selectedContent.like_count || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <i className="ri-chat-3-line"></i>
                      <span>{selectedContent.comment_count || 0}</span>
                    </div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedContent.title}</h2>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer ml-4"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* 본문 */}
            <div className="p-6">
              {/* 작성자 정보 */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedContent.profiles?.full_name || '익명'}</p>
                    <p className="text-sm text-gray-500">{new Date(selectedContent.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleLike}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isLiked
                        ? 'bg-red-100 text-red-600 hover:bg-red-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} mr-1`}></i>
                    좋아요 {selectedContent.like_count || 0}
                  </button>
                </div>
              </div>

              {/* 태그 */}
              {selectedContent.tags && selectedContent.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedContent.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-teal-50 text-teal-600 text-sm rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 내용 */}
              <div className="prose max-w-none mb-6">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedContent.description}</p>
              </div>

              {/* YouTube 영상 임베드 */}
              {selectedContent.content_url && getYouTubeVideoId(selectedContent.content_url) && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">영상</h3>
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedContent.content_url)}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}

              {/* 첨부 파일 */}
              {selectedContent.file_urls && selectedContent.file_urls.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">첨부 파일</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedContent.file_urls.map((url, index) => {
                      const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                      
                      return isImage ? (
                        <div key={index} className="relative group">
                          <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={url}
                              alt={`첨부 이미지 ${index + 1}`}
                              className="w-full h-full object-cover object-top cursor-pointer hover:scale-110 transition-transform duration-300"
                              onClick={() => handleImageClick(url)}
                            />
                          </div>
                        </div>
                      ) : (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <i className="ri-attachment-line text-gray-600"></i>
                          <span className="text-sm text-gray-700 truncate">파일 {index + 1}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 외부 링크 (YouTube가 아닌 경우만 표시) */}
              {selectedContent.content_url && !getYouTubeVideoId(selectedContent.content_url) && (
                <div className="mb-6">
                  <a
                    href={selectedContent.content_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer shadow-sm"
                  >
                    <i className="ri-play-circle-line text-xl"></i>
                    <span>콘텐츠 보기</span>
                  </a>
                </div>
              )}

              {/* 댓글 섹션 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-bold text-gray-900 mb-4">
                  댓글 {selectedContent.comment_count || 0}개
                </h3>

                {/* 댓글 작성 */}
                {user && (
                  <div className="mb-6">
                    {replyTo && (
                      <div className="flex items-center justify-between mb-2 p-2 bg-teal-50 rounded-lg">
                        <span className="text-sm text-teal-600">답글 작성 중...</span>
                        <button
                          onClick={() => setReplyTo(null)}
                          className="text-teal-600 hover:text-teal-700 cursor-pointer"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-teal-600"></i>
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder={replyTo ? "답글을 입력하세요..." : "댓글을 입력하세요..."}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                        />
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            className="px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                          >
                            {replyTo ? '답글 작성' : '댓글 작성'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 댓글 목록 */}
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="space-y-3">
                      {/* 댓글 */}
                      <div className="flex space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-gray-600"></i>
                        </div>
                        <div className="flex-1">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold text-gray-900">{comment.profiles?.full_name || '익명'}</span>
                                <span className="text-xs text-gray-500">
                                  {new Date(comment.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                {user && (
                                  <button
                                    onClick={() => setReplyTo(comment.id)}
                                    className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer"
                                  >
                                    답글
                                  </button>
                                )}
                                {user?.id === comment.user_id && (
                                  <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="text-sm text-red-600 hover:text-red-700 cursor-pointer"
                                  >
                                    삭제
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-gray-700">{comment.comment}</p>
                          </div>
                        </div>
                      </div>

                      {/* 대댓글 */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="ml-12 space-y-3">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="flex space-x-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="ri-user-line text-gray-600 text-sm"></i>
                              </div>
                              <div className="flex-1">
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-semibold text-gray-900 text-sm">{reply.profiles?.full_name || '익명'}</span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(reply.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    {user?.id === reply.user_id && (
                                      <button
                                        onClick={() => handleDeleteComment(reply.id)}
                                        className="text-sm text-red-600 hover:text-red-700 cursor-pointer"
                                      >
                                        삭제
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-gray-700 text-sm">{reply.comment}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {comments.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    첫 댓글을 작성해보세요!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 전체화면 모달 */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={selectedImage}
            alt="전체화면 이미지"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}